import { useState, useEffect, useRef, useCallback } from 'react';
import {
  createConnection,
  normalizeConnection,
  matchesDeepLinkConnection } from
'../utils/connection';

export const useConnection = ({
  api,
  settings,
  refreshLocal,
  refreshRemote,
  addLog,
  setMessage,
  activeTabId
}) => {
  const [connection, setConnection] = useState(createConnection);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [savedLogins, setSavedLogins] = useState([]);
  const [savedLoginsOpen, setSavedLoginsOpen] = useState(false);
  const [savedLoginsTab, setSavedLoginsTab] = useState('local');
  const [vaultPassword, setVaultPassword] = useState('');
  const [vaultUnlocking, setVaultUnlocking] = useState(false);
  const [vaultError, setVaultError] = useState('');
  const [vaultStatus, setVaultStatus] = useState({
    hasMaster: false,
    unlocked: false
  });


  const connectedRef = useRef(false);
  useEffect(() => {
    connectedRef.current = connected;
  }, [connected]);

  const connectWithConfig = useCallback(
    async (config, deepLinkPayload = null, explicitTabId = null) => {
      if (!api) {
        if (setMessage) setMessage('API bridge unavailable.');
        return false;
      }
      if (setMessage) setMessage('');
      const norm = normalizeConnection(config);
      if (deepLinkPayload) {

        if (!matchesDeepLinkConnection(deepLinkPayload, norm)) {

          return false;
        }
      }

      setConnection(norm);
      setConnecting(true);
      try {
        const res = await api.connect(norm, explicitTabId ?? activeTabId);
        if (res.ok) {
          setConnected(true);
          if (addLog) addLog(`Connected to ${norm.host} via ${res.protocol}`);
          await refreshRemote('/');
          return true;
        } else {
          setConnected(false);
          if (setMessage) setMessage(res.error || 'Connection failed');
          if (addLog) addLog(`Connection failed: ${res.error}`);
          return false;
        }
      } catch (err) {
        console.error(err);
        setConnected(false);
        if (setMessage) setMessage(`Connection error: ${err.message}`);
        if (addLog) addLog(`Connection error: ${err.message}`);
        return false;
      } finally {
        setConnecting(false);
      }
    },
    [api, addLog, refreshRemote, setMessage, activeTabId]
  );

  const disconnect = useCallback(async (tabId = activeTabId) => {
    if (!api) return;
    try {
      await api.disconnect(tabId);
      setConnected(false);
      if (addLog) addLog('Disconnected');
    } catch (err) {
      console.error(err);
    }
  }, [api, addLog, activeTabId]);

  const promptLoginForDeepLink = (payload) => {


    const pending = {
      host: payload.host || '',
      port: Number(payload.port) || 2022,
      username: payload.user || payload.username || '',
      password: '',
      protocol: payload.protocol || 'sftp'
    };
    setConnection(pending);

  };


  useEffect(() => {
    if (!api) return;
    const unsubscribe = api.onDeepLink((payload) => {


      if (connectedRef.current) {
        if (matchesDeepLinkConnection(payload, connection)) {

          return;
        }

      } else {



        promptLoginForDeepLink(payload);
      }
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [api, connection]);


  useEffect(() => {
    if (!api) return;
    const bootstrap = async () => {
      try {
        const home = await api.getHomeDir();
        const preferredLocal = settings?.uiux?.defaultLocalPath?.trim();
        if (refreshLocal) {
          await refreshLocal(preferredLocal || home);
        }
        const status = await api.vaultStatus();
        setVaultStatus(status);
        const list = await api.vaultList();
        setSavedLogins(list);
      } catch (err) {
        console.error('Bootstrap failed', err);
      }
    };
    bootstrap();
  }, [api, settings?.uiux?.defaultLocalPath, refreshLocal]);

  const handleVaultUnlock = async () => {
    if (!vaultPassword || !api) return;
    setVaultUnlocking(true);
    setVaultError('');
    try {
      const res = await api.vaultUnlock(vaultPassword);
      if (res && res.ok) {
        const status = await api.vaultStatus();
        setVaultStatus(status);
        const list = await api.vaultList();
        setSavedLogins(list);
        setVaultPassword('');
      } else {
        setVaultError(res?.error || 'Unlock failed');
      }
    } catch (err) {
      setVaultError(err.message);
    } finally {
      setVaultUnlocking(false);
    }
  };

  const saveLogin = async (entry, encrypt) => {
    if (!api || !entry?.name) return false;
    const protocol = entry.protocol || 'sftp';
    const normalized = {
      ...entry,
      protocol,
      port: entry.port || (protocol === 'ftps' ? 21 : 2022),
      description: entry.description || ''
    };
    try {
      const result = await api.vaultSave({
        entry: normalized,
        encrypt
      });
      if (result && result.ok === false) {
        if (setMessage) setMessage(result.error || 'Save failed.');
        return false;
      }
      const list = await api.vaultList();
      setSavedLogins(list);
      setVaultStatus((prev) => ({
        ...prev,
        hasMaster: prev.hasMaster || encrypt,
        unlocked: prev.unlocked || encrypt
      }));
      return true;
    } catch (err) {
      if (setMessage) setMessage(`Save failed: ${err.message}`);
      return false;
    }
  };

  const handleDeleteLogin = async (name) => {
    if (!api || !name) return;
    try {
      await api.vaultDelete(name);
      const list = await api.vaultList();
      setSavedLogins(list);
    } catch (err) {
      if (setMessage) setMessage(`Delete failed: ${err.message}`);
    }
  };

  const connectSavedLogin = async (entry, explicitTabId = null) => {
    if (!api || !entry) return null;
    setVaultError('');
    try {
      let cfg;
      try {
        const resp = await api.vaultGet(entry.name);
        cfg = { path: '', ...resp };
      } catch (innerErr) {
        if (innerErr.message && innerErr.message.toLowerCase().includes('vault locked')) {
          setVaultError('Vault locked. Unlock to view saved logins.');
          return null;
        }
        cfg = { ...entry };
      }

      cfg = normalizeConnection(cfg);

      setConnection(cfg);
      const ok = await connectWithConfig(cfg, null, explicitTabId);
      if (!ok) {
        return null;
      }
      setSavedLoginsOpen(false);
      return cfg;
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('vault locked')) {
        setVaultError('Vault locked. Unlock to view saved logins.');
        return null;
      }
      if (setMessage) setMessage(`Load failed: ${err.message}`);
      return null;
    }
  };

  return {
    connection,
    setConnection,
    connected,
    setConnected,
    connecting,
    setConnecting,
    connectWithConfig,
    disconnect,
    savedLogins,
    setSavedLogins,
    savedLoginsOpen,
    setSavedLoginsOpen,
    savedLoginsTab,
    setSavedLoginsTab,
    vaultPassword,
    setVaultPassword,
    vaultUnlocking,
    vaultError,
    setVaultError,
    vaultStatus,
    setVaultStatus,
    handleVaultUnlock,
    saveLogin,
    handleDeleteLogin,
    connectSavedLogin
  };
};
